import type { ApplicationConfig } from '@angular/core';
import { importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouterModule } from '@angular/router';

import { HTTP_INTERCEPTORS, provideHttpClient, withFetch } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';

import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService,
} from '@azure/msal-angular'; // Redirect component imported from msal-angular

import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '@env/environment';
import { PAGE_ROUTE } from './constants/page-route';
import { ErrorPageType } from './enums/error-page-type';

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: environment.msalConfig.auth.authority,
            knownAuthorities: environment.msalConfig.auth.knownAuthorities,
            redirectUri: environment.msalConfig.auth.redirectUri,
            postLogoutRedirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
            allowNativeBroker: false, // Disables WAM Broker
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Info,
                piiLoggingEnabled: false,
            },
        },
    });
}

export function loggerCallback(logLevel: LogLevel, message: string) {
    // eslint-disable-next-line no-console
    console.log(message);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();

    const scopesArray = [
        ...environment.apiConfig.scopes.read,
        ...environment.apiConfig.scopes.write,
    ];
    protectedResourceMap.set(environment.apiConfig.uri, scopesArray);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    const scopesArray = [
        ...environment.apiConfig.scopes.read,
        ...environment.apiConfig.scopes.write,
    ];
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: scopesArray,
        },
        loginFailedRoute: `/${PAGE_ROUTE.Main}/${PAGE_ROUTE.Error}/${ErrorPageType.Unauthorized}`,
    };
}
//all of the providers and config here wont work on module federation integration
export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(RouterModule.forRoot(routes)),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideHttpClient(withFetch()),
        provideAnimationsAsync(),
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'outline' },
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
};
